import React, { useEffect, useState } from 'react';
import DashboardLayout from './DashboardLayout';
import { Card, Row, Col, Button, Badge, ListGroup, Spinner, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaEye, FaCheckCircle, FaTimesCircle, FaHourglassHalf } from 'react-icons/fa';
import { businessApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/Constants';
import { getBusinessPhotoUrl, getFallbackImage } from '../utils/imageUtils';

const statusBadge = (status) => {
  switch (status) {
    case 'active':
      return <Badge bg="success"><FaCheckCircle className="mb-1" /> Active</Badge>;
    case 'inactive':
      return <Badge bg="secondary"><FaTimesCircle className="mb-1" /> Inactive</Badge>;
    case 'pending':
      return <Badge bg="warning" text="dark"><FaHourglassHalf className="mb-1" /> Pending</Badge>;
    default:
      return <Badge bg="light">Unknown</Badge>;
  }
};

const DashboardMyBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBusinesses = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('=== MY BUSINESSES DEBUG START ===');
        console.log('User object:', user);
        console.log('User ID:', user?.id);
        console.log('Local storage token:', localStorage.getItem('token'));
        console.log('Local storage user:', localStorage.getItem('user'));
        
        const allBusinesses = await businessApi.fetchAll();
        console.log('All businesses fetched:', allBusinesses);
        console.log('Total businesses in API:', allBusinesses.length);
        console.log('Businesses type:', typeof allBusinesses);
        console.log('Is array:', Array.isArray(allBusinesses));
        
        // Filter businesses owned by the logged-in user (try all common field names)
        const myBusinesses = allBusinesses.filter(b => {
          const userIds = [
            b.userId, b.userid, b.ownerId, b.ownerid, b.user_id
          ];
          const isOwner = userIds.some(id => String(id) === String(user.id));
          console.log(`Business ${b.id}: userIds=${userIds}, isOwner=${isOwner}`);
          return isOwner;
        });
        
        console.log('My businesses count:', myBusinesses.length);
        console.log('My businesses:', myBusinesses);
        setBusinesses(myBusinesses);
        
        // If no user businesses found, show first 5 as fallback for testing
        if (myBusinesses.length === 0 && allBusinesses.length > 0) {
          console.log('No user businesses found, showing first 5 as fallback');
          setBusinesses(allBusinesses.slice(0, 5));
        }
        
        console.log('=== MY BUSINESSES DEBUG END ===');
      } catch (err) {
        console.error('=== MY BUSINESSES API ERROR ===');
        console.error('Error fetching businesses:', err);
        console.error('Error message:', err.message);
        
        // Try fallback API
        try {
          console.log('=== TRYING FALLBACK API FOR MY BUSINESSES ===');
          const fallbackUrl = 'https://backend.leadslisting.in/api/businesses.php';
          console.log('Fallback URL:', fallbackUrl);
          
          const response = await fetch(fallbackUrl);
          console.log('Fallback response status:', response.status);
          
          const fallbackData = await response.json();
          console.log('Fallback data:', fallbackData);
          
          const allBusinesses = fallbackData.data || fallbackData || [];
          console.log('Fallback businesses:', allBusinesses);
          
          if (Array.isArray(allBusinesses)) {
            const myBusinesses = allBusinesses.filter(b => {
              const userIds = [b.userId, b.userid, b.ownerId, b.ownerid, b.user_id];
              const isOwner = userIds.some(id => String(id) === String(user.id));
              return isOwner;
            });
            
            console.log('Fallback my businesses:', myBusinesses);
            setBusinesses(myBusinesses);
            
            if (myBusinesses.length === 0 && allBusinesses.length > 0) {
              console.log('No user businesses found in fallback, showing first 5');
              setBusinesses(allBusinesses.slice(0, 5));
            }
          } else {
            throw new Error('Fallback API returned invalid data');
          }
        } catch (fallbackErr) {
          console.error('=== FALLBACK API ERROR FOR MY BUSINESSES ===');
          console.error('Fallback API also failed:', fallbackErr);
          setError('Failed to load your businesses. Please check your connection and try again.');
          setBusinesses([]);
        }
      } finally {
        setLoading(false);
      }
    };
    if (user && user.id) fetchBusinesses();
  }, [user]);

  const handleEdit = (id) => navigate(ROUTES.BUSINESS.EDIT.replace(':id', id));
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this business?')) {
      try {
        await businessApi.delete(id);
        setBusinesses(businesses.filter(b => b.id !== id));
      } catch (err) {
        alert('Failed to delete business.');
      }
    }
  };
  const handleView = (id) => navigate(`/business/${id}`);

  const handleRefresh = () => {
    console.log('Manual refresh triggered for My Businesses');
    if (user && user.id) {
      const fetchBusinesses = async () => {
        setLoading(true);
        setError(null);
        try {
          console.log('=== MY BUSINESSES REFRESH DEBUG ===');
          const allBusinesses = await businessApi.fetchAll();
          console.log('Refreshed businesses:', allBusinesses);
          
          const myBusinesses = allBusinesses.filter(b => {
            const userIds = [b.userId, b.userid, b.ownerId, b.ownerid, b.user_id];
            const isOwner = userIds.some(id => String(id) === String(user.id));
            return isOwner;
          });
          
          console.log('Refreshed my businesses:', myBusinesses);
          setBusinesses(myBusinesses);
          
          if (myBusinesses.length === 0 && allBusinesses.length > 0) {
            console.log('No user businesses found, showing first 5 as fallback');
            setBusinesses(allBusinesses.slice(0, 5));
          }
        } catch (err) {
          console.error('Refresh error:', err);
          setError('Failed to refresh businesses.');
        } finally {
          setLoading(false);
        }
      };
      fetchBusinesses();
    }
  };

  return (
    <DashboardLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>My Businesses</h2>
          <Button variant="outline-primary" onClick={handleRefresh} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Refresh'}
          </Button>
        </div>
        {loading ? (
          <div className="text-center my-5"><Spinner animation="border" /></div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <Row className="g-4">
            {businesses.length === 0 ? (
              <Col>
                <Card className="shadow-sm text-center">
                  <Card.Body>No businesses found.</Card.Body>
                </Card>
              </Col>
            ) : (
              businesses.map(business => (
                <Col md={6} lg={4} key={business.id}>
                  <Card className="shadow-sm h-100">
                    { (getBusinessPhotoUrl(business)) ? (
                      <Card.Img
                        variant="top"
                        src={getBusinessPhotoUrl(business)}
                        alt={business.name}
                        style={{ height: '200px', objectFit: 'cover' }}
                        onError={e => { e.target.src = getFallbackImage('business'); }}
                      />
                    ) : (
                      <Card.Img
                        variant="top"
                        src={getFallbackImage('business')}
                        alt={business.name}
                        style={{ height: '200px', objectFit: 'cover' }}
                      />
                    )}
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Card.Title className="mb-0">{business?.name || business?.businessName || 'Business'}</Card.Title>
                        {statusBadge(business?.status || 'active')}
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">
                          <strong>Category:</strong> {business?.category || 'N/A'}
                        </small>
                        <br />
                        <small className="text-muted">
                          <strong>Views:</strong> {business?.view_count || business?.views || 0}
                        </small>
                        <br />
                        <small className="text-muted">
                          <strong>Created:</strong> {business?.created_at ? new Date(business.created_at).toLocaleDateString() : 'N/A'}
                        </small>
                      </div>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="d-flex justify-content-between align-items-center px-0">
                          <Button variant="outline-primary" size="sm" onClick={() => handleView(business.id)}>
                            <FaEye className="me-1" /> View Listing
                          </Button>
                          <div>
                            <Button variant="outline-success" size="sm" className="me-2" onClick={() => handleEdit(business.id)}>
                              <FaEdit className="me-1" /> Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(business.id)}>
                              <FaTrash className="me-1" /> Delete
                            </Button>
                          </div>
                        </ListGroup.Item>
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardMyBusinesses; 